import {
  Box,
  Stack,
  Text,
  Icon,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  DataTable,
  DataTableTable,
  Label,
  Tooltip,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import type { StatusIndicatorProps } from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthResponse, ServicesApiEnvHealthProcessingCategory } from '@/services/api/services-api';
import {
  CATEGORY_LABELS,
  EGRESS_CATEGORY_LABELS,
  delayStatus,
  formatCompactDuration,
  SERVICE_INFO_TOOLTIPS,
  CATEGORY_IMPACT,
} from '../utils';
import * as styles from './index.css';

const columnHelper = createColumnHelper<ServicesApiEnvHealthProcessingCategory>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Service name',
    cell: (info) => {
      const name = info.getValue();
      const labels = { ...EGRESS_CATEGORY_LABELS, ...CATEGORY_LABELS };

      const displayLabel = labels[name] ?? name;
      const tooltip = SERVICE_INFO_TOOLTIPS[name] ?? displayLabel;
      return (
        <InlineStack gap="050" alignBlock="center">
          <Text text="product-p">{displayLabel}</Text>
          <Tooltip content={tooltip}>
            <Icon name="info" size="sm" color="subtle" />
          </Tooltip>
        </InlineStack>
      );
    },
  }),
  columnHelper.accessor('pending', {
    header: 'Status',
    cell: (info) => {
      const pending = info.getValue();
      const delaySeconds = info.row.original.delay_seconds;
      const status = delayStatus(delaySeconds, pending);
      return (
        <Label color={status.color} showStatusIndicator>
          {status.label}
        </Label>
      );
    },
  }),
];

const STATUS_INDICATOR_COLOR: Record<string, StatusIndicatorProps['color']> = {
  green: 'green',
  yellow: 'yellow',
  red: 'red',
};

function DistressedRow({
  category,
  labels,
}: {
  category: ServicesApiEnvHealthProcessingCategory;
  labels?: Record<string, string>;
}) {
  const label = (labels ?? CATEGORY_LABELS)[category.name] ?? category.name;
  const status = delayStatus(category.delay_seconds, category.pending);
  const duration =
    category.delay_seconds != null && category.delay_seconds >= 10
      ? formatCompactDuration(category.delay_seconds)
      : null;
  const statusText = duration != null ? `${status.label} for ${duration}` : status.label;
  const impact = CATEGORY_IMPACT[category.name];

  return (
    <Stack gap="050" py="150" px="300" className={styles.categoryGroup}>
      <InlineStack gap="200" alignBlock="center">
        <StatusIndicator color={STATUS_INDICATOR_COLOR[status.color] ?? 'yellow'} />
        <Box flex={1}>
          <Text size="s" weight="semibold">
            {label}
          </Text>
        </Box>
        <Text size="xs" color="subtle">
          {statusText}
        </Text>
      </InlineStack>
      {impact != null ? (
        <Box pl="300">
          <Text size="xs" color="subtle">
            {impact}
          </Text>
        </Box>
      ) : null}
    </Stack>
  );
}

function isDistressed(cat: ServicesApiEnvHealthProcessingCategory): boolean {
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color !== 'green';
}

interface ServicesTabContentProps {
  data: ServicesApiEnvHealthResponse;
}

export function ServicesTabContent({ data }: ServicesTabContentProps) {
  const allServices = [...data.processing.categories, ...data.egress.categories];

  const distressedCategories = [
    ...data.processing.categories.filter(isDistressed),
    ...data.egress.categories.filter(isDistressed),
  ];

  return (
    <Stack gap="200">
      {distressedCategories.length > 0 ? (
        <Panel withSectionBorders>
          <PanelHeader>
            <PanelTitle>Services in distress</PanelTitle>
          </PanelHeader>
          <PanelSection withPadding={false}>
            <Stack>
              {distressedCategories.map((cat) => {
                const isEgress = data.egress.categories.includes(cat);
                return (
                  <DistressedRow key={cat.name} category={cat} labels={isEgress ? EGRESS_CATEGORY_LABELS : undefined} />
                );
              })}
            </Stack>
          </PanelSection>
        </Panel>
      ) : null}
      <Panel>
        <PanelHeader>
          <PanelTitle>All services</PanelTitle>
        </PanelHeader>
        <PanelSection withPadding={false}>
          <DataTable data={allServices} columns={columns} caption="All services">
            <DataTableTable />
          </DataTable>
        </PanelSection>
      </Panel>
    </Stack>
  );
}
